import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import lodash from "lodash";
import { toast } from "react-toastify";
import api from "../../BrandOnBoarding/api";
import { fetchShareUsers } from "../../redux/actions/userSentimentActions";
import { AuthContext } from "../../context/auth-context";

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function defaultIndustryChoice(user) {
  if (!user.industries || user.industries.length === 0) return null;
  const def = user.industries.find((i) => i.is_default) || user.industries[0];
  return def?.customer_id ?? null;
}

const DEFAULT_DESCRIPTION = "Creates an independent copy in each recipient's account.";
const DEFAULT_ALREADY_HAS_ACCESS_MESSAGE = "already exists in the source account, no action taken";
const defaultParseShareResponse = (data) => data?.results ?? [];

export default function ShareUserPicker({
  item,
  itemTypeLabel = "template",
  shareEndpoint,
  buildPayload,
  onClose,
  description = DEFAULT_DESCRIPTION,
  excludeUserId,
  parseShareResponse = defaultParseShareResponse,
  alreadyHasAccessMessage = DEFAULT_ALREADY_HAS_ACCESS_MESSAGE,
  filterByCustomer = false,
}) {
  const dispatch = useDispatch();
  const { user: loggedInUser } = useContext(AuthContext);
  const { loading, loaded, data: rawUsers, error } = useSelector((store) => store.shareUsers);

  // Fetched once per session regardless of which caller mounts first — the
  // thunk itself no-ops if already loading/loaded.
  useEffect(() => {
    dispatch(fetchShareUsers());
  }, [dispatch]);

  const allUsers = useMemo(() => {
    let list = rawUsers;
    if (excludeUserId != null) {
      list = list.filter((u) => String(u.id) !== String(excludeUserId));
    }
    if (filterByCustomer) {
      list = list.filter((u) => String(u.customer_id) === String(loggedInUser?.customer_id));
    }
    return list;
  }, [rawUsers, excludeUserId, filterByCustomer, loggedInUser?.customer_id]);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selections, setSelections] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // Debounce the derived filtered list (client-side filter, no network call) —
  // with a few hundred rows, re-filtering + re-rendering on every keystroke
  // can visibly stutter.
  const debouncedSetQuery = useMemo(() => lodash.debounce(setDebouncedQuery, 250), []);
  useEffect(() => {
    debouncedSetQuery(query);
    return () => debouncedSetQuery.cancel();
  }, [query, debouncedSetQuery]);

  // Reset to a clean slate whenever the item being shared changes.
  useEffect(() => {
    const initial = {};
    allUsers.forEach((u) => {
      initial[u.id] = { checked: false, industryChoice: defaultIndustryChoice(u) };
    });
    setSelections(initial);
    setQuery("");
    setDebouncedQuery("");
    setResults(null);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const filteredUsers = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(
      (u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [allUsers, debouncedQuery]);

  const handleToggleUser = (userId) => {
    setSelections((prev) => {
      const cur = prev[userId] || { checked: false, industryChoice: null };
      return { ...prev, [userId]: { ...cur, checked: !cur.checked } };
    });
  };

  const handleIndustryChange = (userId, customerId) => {
    setSelections((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || { checked: false }), industryChoice: customerId },
    }));
  };

  const targets = useMemo(() => {
    return Object.entries(selections)
      .filter(([, sel]) => sel.checked)
      .map(([userId, sel]) => {
        const user = allUsers.find((u) => String(u.id) === String(userId));
        if (!user) return null;
        const customerId =
          !user.industries || user.industries.length === 0 ? user.customer_id : sel.industryChoice;
        if (customerId == null) return null;
        return { user_id: user.id, customer_id: customerId };
      })
      .filter(Boolean);
  }, [selections, allUsers]);

  const handleRetryFetch = () => {
    dispatch(fetchShareUsers());
  };

  const handleShare = async () => {
    if (targets.length === 0 || !item || !shareEndpoint || !buildPayload) return;
    setSubmitting(true);
    try {
      const response = await api.post(shareEndpoint, buildPayload(item, targets));
      setResults(parseShareResponse(response?.data));
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to share ${itemTypeLabel}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const resolveTarget = (r) => {
    const user = allUsers.find((u) => String(u.id) === String(r.user_id));
    const industry = user?.industries?.find((i) => String(i.customer_id) === String(r.customer_id));
    return {
      name: r.name || user?.name || `User #${r.user_id}`,
      industryLabel: industry?.industry || "",
    };
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const showLoading = loading || (!loaded && !error);
  const showRetry = !loading && !loaded && !!error;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!results && description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}

      {results ? (
        // ── Result list ──────────────────────────────────────────────────
        <Stack spacing={1} sx={{ maxHeight: 420, overflowY: "auto" }}>
          {results.map((r, idx) => {
            const { name, industryLabel } = resolveTarget(r);
            if (r.status === "skipped_self") {
              return (
                <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.disabled" }}>
                  <RemoveCircleOutlineIcon fontSize="small" />
                  <Typography variant="body2">
                    {r.message || alreadyHasAccessMessage} — {name}
                  </Typography>
                </Box>
              );
            }
            if (r.status === "failed") {
              return (
                <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, color: "error.main" }}>
                  <CancelIcon fontSize="small" />
                  <Typography variant="body2">
                    {name}
                    {industryLabel ? ` · ${industryLabel}` : ""} — {r.message || "Failed to share"}
                  </Typography>
                </Box>
              );
            }
            const renamed = r.final_name && r.final_name !== item?.name;
            return (
              <Box key={idx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "success.main" }}>
                  <CheckCircleIcon fontSize="small" />
                  <Typography variant="body2" color="text.primary">
                    {r.final_name} → {name}
                    {industryLabel ? ` · ${industryLabel}` : ""}
                  </Typography>
                </Box>
                {renamed && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5, display: "block" }}>
                    renamed to avoid a duplicate
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      ) : (
        // ── Search + user list ───────────────────────────────────────────
        <>
          <TextField
            placeholder="name@company.com"
            size="small"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ maxHeight: 380, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 1 }}>
            {showLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : showRetry ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 4 }}>
                <Typography variant="body2" color="error">
                  Failed to load users.
                </Typography>
                <Button size="small" onClick={handleRetryFetch}>
                  Retry
                </Button>
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {debouncedQuery ? `No users match "${debouncedQuery}"` : "No users available."}
                </Typography>
              </Box>
            ) : (
              filteredUsers.map((user) => {
                const sel = selections[user.id] || { checked: false, industryChoice: defaultIndustryChoice(user) };
                return (
                  <Box
                    key={user.id}
                    onClick={() => handleToggleUser(user.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9",
                      "&:hover": { backgroundColor: "#f9fafb" },
                    }}
                  >
                    <Checkbox
                      checked={sel.checked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggleUser(user.id)}
                      size="small"
                    />
                    <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem" }}>
                      {getInitials(user.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                        {user.email}
                      </Typography>
                    </Box>
                    {user.industries?.length > 0 && (
                      <Select
                        size="small"
                        value={sel.industryChoice ?? ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleIndustryChange(user.id, e.target.value)}
                        sx={{ minWidth: 160, fontSize: "0.8rem" }}
                      >
                        {user.industries.map((ind) => (
                          <MenuItem key={ind.customer_id} value={ind.customer_id}>
                            {ind.industry}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          py: 1.5,
          position: "sticky",
          bottom: 0,
          backgroundColor: "background.paper",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        {results ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={targets.length === 0 || submitting}
              onClick={handleShare}
              startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {submitting ? "Sharing…" : `Share to ${targets.length}`}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
